import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { Integration, IntegrationLog } from '../types';

const GOOGLE_PROVIDERS = [
  {
    key: 'google_auth',
    label: 'Google Login',
    icon: 'ri-google-line',
    accent: '#4285F4',
    description: 'Permite que usuários façam login com conta Google (OAuth 2.0)',
    defaultScopes: ['openid', 'email', 'profile'],
  },
  {
    key: 'google_calendar',
    label: 'Google Calendar',
    icon: 'ri-calendar-2-line',
    accent: '#0F9D58',
    description: 'Sincronização de eventos e calendários com Google Calendar API',
    defaultScopes: ['https://www.googleapis.com/auth/calendar'],
  },
];

const STATUS_META = {
  connected: { label: 'Conectado',   color: 'bg-emerald-500/20 text-emerald-400', icon: 'ri-checkbox-circle-line' },
  error:     { label: 'Erro',        color: 'bg-red-500/20 text-red-400',         icon: 'ri-error-warning-line' },
  untested:  { label: 'Não testado', color: 'bg-white/10 text-white/40',          icon: 'ri-question-line' },
};

interface Props {
  integrations: Integration[];
  onSave: (id: string, data: Partial<Integration>) => Promise<void>;
  onTest: (id: string, type: string) => Promise<{ ok: boolean; message: string }>;
  logs: IntegrationLog[];
}

export const GoogleIntegration = ({ integrations, onSave, onTest, logs }: Props) => {
  const [activeProvider, setActiveProvider] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [newScope, setNewScope] = useState('');
  const [liveStatus, setLiveStatus] = useState<'checking' | 'enabled' | 'disabled' | 'unknown'>('unknown');

  const current = integrations.find((i) => i.provider === activeProvider);
  const providerMeta = GOOGLE_PROVIDERS.find((p) => p.key === activeProvider);
  const currentLogs = logs.filter((l) => l.integration_id === current?.id).slice(0, 6);

  // Get Supabase project ref from URL
  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const projectRef = supabaseUrl?.replace('https://', '').replace('.supabase.co', '') || '';
  const supabaseCallbackUrl = `${supabaseUrl}/auth/v1/callback`;

  useEffect(() => {
    if (current) setEditing({ ...current.config_json });
  }, [activeProvider, current?.id]);

  // Live check: verify if Google provider is actually enabled in Supabase Auth
  useEffect(() => {
    if (activeProvider !== 'google_auth') { setLiveStatus('unknown'); return; }
    let cancelled = false;
    setLiveStatus('checking');
    (async () => {
      try {
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { skipBrowserRedirect: true },
        });
        if (cancelled) return;
        const msg = error?.message?.toLowerCase() || '';
        if (!error && data?.url) {
          setLiveStatus('enabled');
        } else if (msg.includes('provider is not enabled') || msg.includes('unsupported provider')) {
          setLiveStatus('disabled');
        } else {
          setLiveStatus('unknown');
        }
      } catch {
        if (!cancelled) setLiveStatus('unknown');
      }
    })();
    return () => { cancelled = true; };
  }, [activeProvider]);

  const handleSave = async () => {
    if (!current) return;
    setSaving(true);
    await onSave(current.id, { config_json: editing });
    setSaving(false);
  };

  const handleTest = async () => {
    if (!current) return;
    setTesting(true);
    setTestResult(null);
    const result = await onTest(current.id, 'test_oauth');
    setTestResult(result);
    // Re-run live check after test
    if (activeProvider === 'google_auth') {
      setLiveStatus('checking');
      try {
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { skipBrowserRedirect: true },
        });
        const msg = error?.message?.toLowerCase() || '';
        if (!error && data?.url) setLiveStatus('enabled');
        else if (msg.includes('provider is not enabled') || msg.includes('unsupported provider')) setLiveStatus('disabled');
        else setLiveStatus('unknown');
      } catch {
        setLiveStatus('unknown');
      }
    }
    setTesting(false);
  };

  const addScope = () => {
    if (!newScope.trim()) return;
    const scopes = (editing.scopes as string[]) || [];
    if (!scopes.includes(newScope.trim())) {
      setEditing({ ...editing, scopes: [...scopes, newScope.trim()] });
    }
    setNewScope('');
  };

  const removeScope = (scope: string) => {
    const scopes = (editing.scopes as string[]) || [];
    setEditing({ ...editing, scopes: scopes.filter((s) => s !== scope) });
  };

  const statusMeta = (status: string) => STATUS_META[status as keyof typeof STATUS_META] || STATUS_META.untested;

  const liveStatusConfig = {
    checking: { color: 'border-white/20 bg-white/5', icon: 'ri-loader-4-line animate-spin text-white/40', text: 'Verificando provider no Supabase Auth...', textColor: 'text-white/50' },
    enabled:  { color: 'border-emerald-500/30 bg-emerald-500/10', icon: 'ri-checkbox-circle-fill text-emerald-400', text: 'Provider Google HABILITADO no Supabase Auth — login funcionando', textColor: 'text-emerald-300' },
    disabled: { color: 'border-red-500/30 bg-red-500/10', icon: 'ri-close-circle-fill text-red-400', text: 'Provider Google DESABILITADO no Supabase Auth — login vai falhar', textColor: 'text-red-300' },
    unknown:  { color: 'border-white/10 bg-white/5', icon: 'ri-question-line text-white/40', text: 'Status do provider não verificado', textColor: 'text-white/40' },
  }[liveStatus];

  return (
    <div className="space-y-6">
      {/* Provider cards */}
      <div className="grid grid-cols-2 gap-4">
        {GOOGLE_PROVIDERS.map((p) => {
          const integ = integrations.find((i) => i.provider === p.key);
          const sm = statusMeta(integ?.last_status || 'untested');
          return (
            <button
              key={p.key}
              onClick={() => { setActiveProvider(activeProvider === p.key ? null : p.key); setTestResult(null); }}
              className={`text-left border rounded-2xl p-5 transition-all cursor-pointer ${
                activeProvider === p.key ? 'border-violet-500/50' : 'border-white/10 hover:border-white/20'
              }`}
              style={{ background: activeProvider === p.key ? `${p.accent}12` : `${p.accent}08` }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ background: `${p.accent}20` }}>
                  <i className={`${p.icon} text-[22px]`} style={{ color: p.accent }} />
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <span className={`text-[11px] font-700 px-2.5 py-1 rounded-full flex items-center gap-1 ${sm.color}`}>
                    <i className={`${sm.icon} text-[12px]`} />
                    {sm.label}
                  </span>
                  {integ?.is_active && (
                    <span className="text-[10px] font-700 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">Ativo</span>
                  )}
                </div>
              </div>
              <h3 className="text-[15px] font-700 text-white mb-1">{p.label}</h3>
              <p className="text-[12px] text-white/40 leading-relaxed">{p.description}</p>
            </button>
          );
        })}
      </div>

      {/* Setup instructions */}
      {activeProvider === 'google_auth' && (
        <div className="space-y-4">
          {/* Live Status Badge */}
          <div className={`border rounded-2xl p-4 flex items-center gap-3 ${liveStatusConfig.color}`}>
            <i className={`text-[20px] flex-shrink-0 ${liveStatusConfig.icon}`} />
            <div>
              <p className={`text-[13px] font-700 ${liveStatusConfig.textColor}`}>{liveStatusConfig.text}</p>
              {liveStatus === 'disabled' && (
                <p className="text-[11px] text-white/40 mt-0.5">
                  Siga os passos abaixo para habilitar. Esta verificação é em tempo real.
                </p>
              )}
            </div>
          </div>

          {/* Critical step: Supabase Dashboard */}
          <div className={`border rounded-2xl p-5 ${liveStatus === 'disabled' ? 'border-red-500/30 bg-red-500/5' : 'border-violet-500/20 bg-violet-500/10'}`}>
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 flex items-center justify-center rounded-xl shrink-0 mt-0.5 ${liveStatus === 'disabled' ? 'bg-red-500/20' : 'bg-violet-500/20'}`}>
                <i className={`ri-shield-star-line text-[16px] ${liveStatus === 'disabled' ? 'text-red-400' : 'text-violet-400'}`} />
              </div>
              <div className="flex-1">
                <p className={`text-[13px] font-700 mb-1 ${liveStatus === 'disabled' ? 'text-red-300' : 'text-violet-300'}`}>
                  Passo 1 — Habilitar provider Google no Supabase Auth (obrigatório)
                </p>
                <p className="text-[12px] text-white/50 mb-3">
                  Sem isso, qualquer tentativa de login com Google retorna "Unsupported provider". Esta configuração
                  é feita no painel do Supabase, não aqui.
                </p>
                <ol className="text-[12px] text-white/50 space-y-2 list-decimal list-inside mb-3">
                  <li>
                    Acesse{' '}
                    <a
                      href={`https://supabase.com/dashboard/project/${projectRef}/auth/providers`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-violet-300 hover:text-violet-200 underline"
                    >
                      Authentication → Providers no seu projeto Supabase
                    </a>
                  </li>
                  <li>Encontre <strong className="text-white/70">Google</strong> e ative o toggle <strong className="text-violet-300">"Enable Sign in with Google"</strong></li>
                  <li>Cole o <strong className="text-white/70">Client ID</strong> e <strong className="text-white/70">Client Secret</strong> do Google Cloud Console</li>
                  <li>Salve as alterações</li>
                </ol>

                <div className="space-y-2">
                  <div className="bg-black/20 rounded-xl p-3">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider font-700 mb-1">Callback URL do Supabase (adicionar no Google Cloud):</p>
                    <div className="flex items-center gap-2">
                      <p className="text-[12px] text-violet-300 font-mono flex-1 break-all">{supabaseCallbackUrl}</p>
                      <button
                        onClick={() => navigator.clipboard.writeText(supabaseCallbackUrl)}
                        className="px-2 py-1 bg-white/10 hover:bg-white/15 rounded-lg text-[11px] text-white/50 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                      >
                        <i className="ri-file-copy-line" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2: Redirect URLs */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-xl shrink-0 mt-0.5">
                <i className="ri-link text-amber-400 text-[16px]" />
              </div>
              <div className="flex-1">
                <p className="text-[13px] font-700 text-amber-300 mb-1">
                  Passo 2 — Adicionar Redirect URLs no Supabase (para produção)
                </p>
                <p className="text-[12px] text-white/50 mb-3">
                  Sem a URL do seu app na lista de redirect URLs, o callback após o Google Login vai falhar em produção.
                </p>
                <ol className="text-[12px] text-white/50 space-y-2 list-decimal list-inside mb-3">
                  <li>
                    Acesse{' '}
                    <a
                      href={`https://supabase.com/dashboard/project/${projectRef}/auth/url-configuration`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-300 hover:text-amber-200 underline"
                    >
                      Authentication → URL Configuration no Supabase
                    </a>
                  </li>
                  <li>Em <strong className="text-white/70">"Redirect URLs"</strong>, adicione a URL do seu app publicado</li>
                </ol>
                <div className="bg-black/20 rounded-xl p-3 space-y-1">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider font-700 mb-2">URLs que devem estar na lista:</p>
                  {['https://app.pandapro.com.br', 'https://app.pandapro.com.br/**'].map((url) => (
                    <div key={url} className="flex items-center gap-2">
                      <p className="text-[12px] text-amber-300 font-mono flex-1">{url}</p>
                      <button
                        onClick={() => navigator.clipboard.writeText(url)}
                        className="px-2 py-1 bg-white/10 hover:bg-white/15 rounded-lg text-[11px] text-white/50 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                      >
                        <i className="ri-file-copy-line" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Google Cloud Console */}
          <div className="bg-sky-500/10 border border-sky-500/20 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-sky-500/20 rounded-xl shrink-0 mt-0.5">
                <i className="ri-information-line text-sky-400 text-[16px]" />
              </div>
              <div>
                <p className="text-[13px] font-700 text-sky-300 mb-1">Passo 3 — Criar credenciais no Google Cloud Console</p>
                <ol className="text-[12px] text-white/50 space-y-1.5 list-decimal list-inside">
                  <li>
                    Acesse{' '}
                    <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:text-sky-200 underline">
                      console.cloud.google.com → APIs &amp; Serviços → Credenciais
                    </a>
                  </li>
                  <li>Clique em <strong className="text-white/70">Criar credenciais → ID do cliente OAuth 2.0</strong></li>
                  <li>Tipo: <strong className="text-white/70">Aplicativo da Web</strong></li>
                  <li>
                    Em <strong className="text-white/70">"URIs de redirecionamento autorizados"</strong>, adicione:
                    <span className="block mt-1 font-mono text-sky-300">{supabaseCallbackUrl}</span>
                  </li>
                  <li>Copie o <strong className="text-sky-300">Client ID</strong> e o <strong className="text-sky-300">Client Secret</strong> e cole no Supabase (Passo 1)</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeProvider === 'google_calendar' && (
        <div className="bg-sky-500/10 border border-sky-500/20 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-sky-400 text-[18px] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[13px] font-700 text-sky-300 mb-2">Como configurar o Google Calendar</p>
              <ol className="text-[12px] text-white/50 space-y-1.5 list-decimal list-inside">
                <li>Acesse o <strong className="text-white/70">Google Cloud Console</strong></li>
                <li>Habilite a <strong className="text-white/70">Google Calendar API</strong> no projeto</li>
                <li>Crie credenciais OAuth 2.0 do tipo Aplicativo da Web</li>
                <li>Adicione a URI de redirecionamento da Edge Function do Supabase</li>
                <li>Copie o <strong className="text-sky-300">Client ID</strong> e o <strong className="text-sky-300">Client Secret</strong></li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Config panel */}
      {activeProvider && current && providerMeta && (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <i className={`${providerMeta.icon} text-[18px] text-white/60`} />
              <h2 className="text-[15px] font-700 text-white">
                Configurar {providerMeta.label}
                {activeProvider === 'google_auth' && (
                  <span className="ml-2 text-[11px] font-500 text-white/30">(referência — configuração real no Supabase Auth)</span>
                )}
              </h2>
            </div>
            <button onClick={() => { setActiveProvider(null); setTestResult(null); }} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer">
              <i className="ri-close-line text-white/60" />
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-5">
                <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider">OAuth 2.0</p>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Client ID</label>
                  <input
                    type="text"
                    value={String(editing.client_id || '')}
                    onChange={(e) => setEditing({ ...editing, client_id: e.target.value })}
                    placeholder="xxxxxxxxxxxx-xxxx.apps.googleusercontent.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                  />
                  {activeProvider === 'google_auth' && (
                    <p className="text-[10px] text-white/25 mt-1">Salvar aqui é apenas para referência. Cole estes valores no Supabase Auth para funcionar.</p>
                  )}
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Client Secret</label>
                  <div className="relative">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={String(editing.client_secret || '')}
                      onChange={(e) => setEditing({ ...editing, client_secret: e.target.value })}
                      placeholder="GOCSPX-..."
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 pr-10"
                    />
                    <button onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer" type="button">
                      <i className={`${showSecret ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-[14px]`} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">
                    {activeProvider === 'google_auth' ? 'Callback URL do Supabase' : 'Redirect URI'}
                  </label>
                  <input
                    type="text"
                    value={activeProvider === 'google_auth' ? supabaseCallbackUrl : String(editing.redirect_uri || '')}
                    onChange={(e) => activeProvider !== 'google_auth' && setEditing({ ...editing, redirect_uri: e.target.value })}
                    readOnly={activeProvider === 'google_auth'}
                    className={`w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 ${activeProvider === 'google_auth' ? 'text-white/40 cursor-default' : 'text-white'}`}
                  />
                  <p className="text-[10px] text-white/25 mt-1.5">
                    {activeProvider === 'google_auth'
                      ? 'Adicione esta URL como URI de redirecionamento autorizado no Google Cloud Console'
                      : 'URI que deve ser adicionada no Google Cloud Console como URI de redirecionamento autorizado'}
                  </p>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-white/5">
                  <div>
                    <p className="text-[13px] text-white font-600">Integração ativa</p>
                    <p className="text-[11px] text-white/30">Habilitar esta integração no sistema</p>
                  </div>
                  <button
                    onClick={async () => await onSave(current.id, { is_active: !current.is_active })}
                    className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative flex-shrink-0 overflow-hidden ${current.is_active ? 'bg-violet-600' : 'bg-white/10'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${current.is_active ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>
              </div>

              <div className="space-y-5">
                {/* Scopes */}
                <div>
                  <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider mb-3">Escopos de Permissão</p>
                  <div className="space-y-2 mb-3">
                    {((editing.scopes as string[]) || []).map((scope) => (
                      <div key={scope} className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2">
                        <span className="text-[11px] text-white/60 font-mono truncate">{scope}</span>
                        <button onClick={() => removeScope(scope)} className="ml-2 w-6 h-6 flex items-center justify-center bg-white/5 hover:bg-red-500/20 rounded-lg cursor-pointer flex-shrink-0 transition-colors">
                          <i className="ri-close-line text-white/40 text-[12px]" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newScope}
                      onChange={(e) => setNewScope(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addScope()}
                      placeholder="Adicionar escopo personalizado..."
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-[12px] outline-none focus:border-violet-500 placeholder:text-white/20"
                    />
                    <button onClick={addScope} className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/50 text-[12px] transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-add-line" />
                    </button>
                  </div>
                </div>

                {/* Status */}
                <div className={`rounded-xl p-4 border ${current.last_status === 'connected' ? 'bg-emerald-500/10 border-emerald-500/20' : current.last_status === 'error' ? 'bg-red-500/10 border-red-500/20' : 'bg-white/5 border-white/10'}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <i className={`${statusMeta(current.last_status).icon} text-[15px] ${statusMeta(current.last_status).color.split(' ')[1]}`} />
                    <span className="text-[12px] font-700 text-white">Último teste registrado</span>
                  </div>
                  <p className="text-[11px] text-white/50">
                    {current.last_status === 'connected' && 'Provider verificado e funcional no Supabase Auth'}
                    {current.last_status === 'error' && (current.last_error || 'Erro nas credenciais')}
                    {current.last_status === 'untested' && 'Clique em "Verificar Provider" para checar o status real'}
                  </p>
                </div>

                {testResult && (
                  <div className={`rounded-xl p-3 flex items-start gap-2 ${testResult.ok ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                    <i className={`text-[14px] mt-0.5 flex-shrink-0 ${testResult.ok ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-error-warning-line text-red-400'}`} />
                    <p className="text-[12px] text-white/70">{testResult.message}</p>
                  </div>
                )}

                <button
                  onClick={handleTest}
                  disabled={testing}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/70 text-[13px] font-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${testing ? 'ri-loader-4-line animate-spin' : 'ri-wifi-line'} text-[14px]`} />
                  {testing ? 'Verificando...' : activeProvider === 'google_auth' ? 'Verificar Provider no Supabase' : 'Verificar Credenciais'}
                </button>

                {currentLogs.length > 0 && (
                  <div>
                    <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider mb-2">Histórico</p>
                    <div className="space-y-1.5">
                      {currentLogs.map((log) => (
                        <div key={log.id} className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2">
                          <div className="flex items-center gap-2">
                            <i className={`text-[12px] ${log.status === 'success' ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-close-circle-line text-red-400'}`} />
                            <span className="text-[12px] text-white/60">{log.event_type}</span>
                          </div>
                          <span className="text-[11px] text-white/30">{new Date(log.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/10">
              <button onClick={() => { setActiveProvider(null); setTestResult(null); }} className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/60 text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving ? 'Salvando...' : 'Salvar Referência'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
